
import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ExternalLink } from "lucide-react";
import { useTranslation } from 'react-i18next';

type Alternative = {
  us: string;
  eu: string;
  description: string;
  country: string;
  selfHosted: boolean;
  url: string;
  logo?: string;
  platform?: string;
  openSource?: boolean;
  descriptionKey?: string; // Key for translation lookup
  category?: string;
};

type AlternativesListProps = {
  title: string;
  description: string;
  alternatives: Alternative[];
  categoryId?: string;
};

const AlternativesList = ({ title, description, alternatives, categoryId }: AlternativesListProps) => {
  const { t, i18n } = useTranslation(['alternativ', 'altDescriptions']);
  
  // Helper function to get the description
  const getDescription = (alt: Alternative) => {
    // If we have a descriptionKey and categoryId, try to use the translated version
    if (alt.descriptionKey && categoryId) {
      const translationKey = `${categoryId}.${alt.descriptionKey}`;
      const translatedDesc = t(translationKey, { ns: 'altDescriptions' });
      
      // If we got back the translation key, it means no translation was found
      if (translatedDesc !== translationKey) {
        return translatedDesc;
      }
    }
    
    // Fall back to the hardcoded description
    return alt.description;
  };
  
  return (
    <div className="space-y-8">
      <div className="max-w-2xl mx-auto text-center">
        <h2 className="text-2xl font-bold text-frikopplad-navy mb-3">{title}</h2>
        <p className="text-gray-600">{description}</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {alternatives.map((alt, index) => (
          <Card key={index} className="overflow-hidden border border-gray-100 hover:shadow-md transition-shadow duration-300">
            <CardContent className="p-0">
              <div className="p-6">
                <div className="flex flex-col sm:flex-row justify-between mb-4">
                  <div className="mb-3 sm:mb-0">
                    <div className="text-sm text-frikopplad-burgundy font-medium mb-1">{t('ui.replace', { ns: 'alternativ' })}</div>
                    <div className="font-bold text-gray-800">{alt.us}</div>
                  </div>
                  <div>
                    <div className="text-sm text-frikopplad-blue font-medium mb-1">{t('ui.with', { ns: 'alternativ' })}</div>
                    <div className="font-bold text-frikopplad-navy">{alt.eu}</div>
                  </div>
                </div>
                <p className="text-gray-600 mb-4">{getDescription(alt)}</p>
                
                <div className="flex flex-wrap items-center justify-between mt-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Country badge - make it yellow if US-based */}
                    <Badge 
                      variant="outline" 
                      className={`${alt.country.includes('USA') ? 'bg-[#FEF7CD]' : 'bg-frikopplad-cream/50'} 
                        text-frikopplad-navy border-frikopplad-cream/70`}
                    >
                      {t(`countries.${alt.country}`, { ns: 'alternativ', defaultValue: alt.country })}
                    </Badge>
                    
                    {/* Platform badge if available */}
                    {alt.platform && (
                      <Badge variant="outline" className="bg-frikopplad-navy/10 text-frikopplad-navy border-frikopplad-navy/20">
                        {t(`platforms.${alt.platform}`, { ns: 'alternativ', defaultValue: alt.platform })}
                      </Badge>
                    )}
                    
                    {/* Self-hosted badge */}
                    {alt.selfHosted && (
                      <Badge variant="outline" className="bg-frikopplad-blue/10 text-frikopplad-blue border-frikopplad-blue/20">
                        {t('badges.selfhosted', { ns: 'alternativ' })}
                      </Badge>
                    )}
                    
                    {/* Open source badge */}
                    {alt.openSource && (
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-100">
                        {t('badges.opensrc', { ns: 'alternativ' })}
                      </Badge>
                    )}
                  </div>
                  
                  {alt.url && (
                    <a 
                      href={alt.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-frikopplad-blue hover:text-frikopplad-navy flex items-center gap-1 text-sm font-medium"
                    >
                      {t('ui.visit', { ns: 'alternativ' })} <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AlternativesList;
